import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { BaseApiService } from '../api/base-api.service';
import { PaginatedResponse } from '../../models/api/api-response.model';
import { TaskLog } from '../../entities/task/task-log.entities';
import {
    TaskLogQueryParams,
    TaskLogFilters,
    TaskLogCreateRequest,
    TaskLogUpdateRequest,
    TaskLogApiResponse,
    TaskLogStatistics
} from '../../entities/task/task-log-api.types';

@Injectable({
    providedIn: 'root'
})
export class TaskLogService extends BaseApiService {

    /**
     * Get all task logs with pagination and optional filters
     */
    getLogs(params?: TaskLogQueryParams, filters?: TaskLogFilters): Observable<PaginatedResponse<TaskLog>> {
        // Get farm_id from filters - it's required for the path
        const farmId = filters?.farm_id;
        if (!farmId) {
            throw new Error('Farm ID is required to get task logs');
        }

        let httpParams = new HttpParams();

        // Add required search parameter (API requires 'q' parameter)
        if (params?.q && params.q.trim() !== '') {
            httpParams = httpParams.set('q', params.q.trim());
        }

        if (params) {
            if (params.skip !== undefined) {
                httpParams = httpParams.set('skip', params.skip.toString());
            }
            if (params.limit !== undefined) {
                httpParams = httpParams.set('limit', params.limit.toString());
            }
        }

        // Add other filters as query parameters (excluding farm_id which goes in path)
        if (filters) {
            if (filters.task_id) {
                httpParams = httpParams.set('_task_id', filters.task_id);
            }
            if (filters.user_id) {
                httpParams = httpParams.set('_user_id', filters.user_id);
            }
            if (filters.action) {
                httpParams = httpParams.set('_action', filters.action);
            }
            if (filters.status) {
                httpParams = httpParams.set('_status', filters.status);
            }
        }

        const endpoint = `/farms/${farmId}/tasks/logs`;
        return this.getPaginated<TaskLog>(endpoint, httpParams);
    }

    /**
     * Get task log by ID
     */
    getLogById(id: string, farmId: string): Observable<TaskLogApiResponse> {
        return this.get<TaskLogApiResponse>(`/farms/${farmId}/tasks/logs/${id}`);
    }

    /**
     * Create new task log
     */
    createLog(log: TaskLogCreateRequest, farmId: string): Observable<TaskLogApiResponse> {
        return this.post<TaskLogApiResponse>(`/farms/${farmId}/tasks/logs`, log);
    }

    /**
     * Update task log
     */
    updateLog(id: string, updates: TaskLogUpdateRequest, farmId: string): Observable<TaskLogApiResponse> {
        return this.put<TaskLogApiResponse>(`/farms/${farmId}/tasks/logs/${id}`, updates);
    }

    /**
     * Partially update task log
     */
    patchLog(id: string, updates: Partial<TaskLogUpdateRequest>, farmId: string): Observable<TaskLogApiResponse> {
        return this.patch<TaskLogApiResponse>(`/farms/${farmId}/tasks/logs/${id}`, updates);
    }

    /**
     * Delete task log
     */
    deleteLog(id: string, farmId: string): Observable<void> {
        return this.delete<void>(`/farms/${farmId}/tasks/logs/${id}`);
    }

    /**
     * Get logs by task ID
     */
    getLogsByTask(taskId: string, params?: TaskLogQueryParams): Observable<PaginatedResponse<TaskLog>> {
        return this.getLogs(params, { task_id: taskId });
    }

    /**
     * Get logs by farm ID
     */
    getLogsByFarm(farmId: string, params?: TaskLogQueryParams): Observable<PaginatedResponse<TaskLog>> {
        return this.getLogs(params, { farm_id: farmId });
    }

    /**
     * Get logs by user ID
     */
    getLogsByUser(userId: string, params?: TaskLogQueryParams): Observable<PaginatedResponse<TaskLog>> {
        return this.getLogs(params, { user_id: userId });
    }

    /**
     * Get logs by action
     */
    getLogsByAction(action: string, params?: TaskLogQueryParams): Observable<PaginatedResponse<TaskLog>> {
        return this.getLogs(params, { action });
    }

    /**
     * Get logs by status
     */
    getLogsByStatus(status: string, params?: TaskLogQueryParams): Observable<PaginatedResponse<TaskLog>> {
        return this.getLogs(params, { status });
    }

    /**
     * Get log statistics for a task
     */
    getLogStatistics(taskId: string): Observable<TaskLogStatistics> {
        return this.get<TaskLogStatistics>(`/tasks/logs/statistics/${taskId}`);
    }
}
